import React, { useState } from 'react';
import T from '../common/T';
import Price from '../common/Price'; // <-- ADDED THIS IMPORT (Adjust path if needed)
import { useCRM } from '../../context/CRMContext';
import { Phone, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import LeadViewModal from './LeadViewModel';
import { useAuth } from '../../context/AuthContext';
import DataTable from '../common/DataTables.jsx';
import TableActionButtons from '../common/TableActionButtons';

const CRMListView = () => {
  const { columns, deleteLead } = useCRM();
  const navigate = useNavigate();
  const { checkAccess } = useAuth();

  const [selectedLead, setSelectedLead] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const canEdit = checkAccess('crm.edit');
  const canDelete = checkAccess('crm.delete');
  const canViewFinancials = checkAccess('financial.view');

  // FLATTEN STAGES INTO A SINGLE ARRAY FOR DATATABLE
  const flattenedLeads = Object.entries(columns || {}).flatMap(([stageId, stage]) => {
    const rawItems = Array.isArray(stage.items) ? stage.items : [];
    return rawItems
      .filter(item => item && item.id)
      .map(item => ({
        ...item,
        stageName: stage.title,
        stageId
      }));
  });

  const handleViewClick = (lead) => {
    setSelectedLead(lead);
    setIsViewModalOpen(true);
  };

  const handleEditClick = (lead) => {
    navigate('/crm/detail', { state: { lead } });
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this lead?")) {
      if (deleteLead) deleteLead(id);
      toast.success("Lead deleted successfully");
    }
  };

  const getStageBadge = (stageId, stageName) => {
    let styles = "bg-gray-50 dark:bg-[#21262d] text-gray-600 dark:text-[#848d97] border-gray-200 dark:border-[#30363d]";
    if (stageId === 'sold') styles = "bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800/60";
    if (stageId === 'lost') styles = "bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800/60";
    if (stageId === 'booked') styles = "bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-800/60";
    if (stageId === 'new_inquiry') styles = "bg-gray-100 dark:bg-[#21262d] text-gray-700 dark:text-[#848d97] border-gray-300 dark:border-[#30363d]";
    
    return (
        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border ${styles}`}>
            {stageName}
        </span>
    );
  };

  const tableColumns = [
    {
      header: "Client Name",
      accessor: "client",
      sortable: true,
      render: (lead) => (
        <div>
          <button 
             onClick={() => canEdit ? handleEditClick(lead) : handleViewClick(lead)} 
             className="font-bold text-gray-900 dark:text-[#f0f6fc] hover:text-[#D9531E] dark:hover:text-[#D9531E] hover:underline text-left transition-colors"
          >
             {lead.client || lead.company}
          </button>
          <div className="text-[10px] text-gray-400 dark:text-[#6e7681] font-mono mt-0.5">ID: #{lead.id.toString().slice(-4)}</div>
        </div>
      )
    },
    {
      header: "Requirement / Unit",
      accessor: "title",
      sortable: true,
      render: (lead) => (
        <div>
          <div className="font-semibold text-gray-800 dark:text-[#f0f6fc]">{lead.unit || 'General Inquiry'}</div>
          <div className="text-[10px] text-gray-500 dark:text-[#848d97] truncate max-w-[200px]">{lead.title}</div>
        </div>
      )
    },
    {
      header: "Contact",
      render: (lead) => (
        <div className="flex flex-col gap-1">
          {lead.phone && (
            <div className="flex items-center gap-1 text-[11px] font-medium text-gray-700 dark:text-[#848d97]">
              <Phone size={10} className="text-gray-400 dark:text-[#6e7681]" /> {lead.phone}
            </div>
          )}
          {lead.email && (
            <div className="flex items-center gap-1 text-[10px] text-gray-500 dark:text-[#6e7681]">
              <MessageSquare size={10} className="text-gray-400 dark:text-[#6e7681]" /> {lead.email}
            </div>
          )}
        </div>
      )
    },
    // Conditionally inject Budget Column based on permissions
    ...(canViewFinancials ? [{
      header: "Budget",
      accessor: "budget",
      align: "right",
      sortable: true,
      render: (lead) => <span className="font-bold text-gray-900 dark:text-[#f0f6fc]"><Price amount={(Number(lead.budget) || 0)} /></span>
    }] : []),
    {
      header: "Stage",
      accessor: "stageName",
      align: "center",
      sortable: true,
      render: (lead) => getStageBadge(lead.stageId, lead.stageName)
    },
    {
      header: "Actions",
      align: "right",
      render: (lead) => (
        <TableActionButtons
          onView={checkAccess('crm.view') ? () => handleViewClick(lead) : undefined}
          onEdit={canEdit ? () => handleEditClick(lead) : undefined}
          onDelete={canDelete ? () => handleDelete(lead.id) : undefined}
        />
      )
    }
  ];

  return (
    <>
      <div className="relative">
        <DataTable data={flattenedLeads} columns={tableColumns} emptyMessage="No inquiries found." />
      </div>

      <LeadViewModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        lead={selectedLead}
      />
    </>
  );
};

export default CRMListView;